  import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock, User, Loader2 } from "lucide-react";
import { trackEvent, trackException } from "@/lib/firebase";

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://finease-crm.vercel.app';

export default function BlogPage() {
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState(["All"]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBlogs();
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchBlogs(selectedCategory);
  }, [selectedCategory]);

  const fetchBlogs = async (category = "All") => {
    try {
      setLoading(true);
      const url = category === "All"
        ? `${API_BASE_URL}/api/blogs`
        : `${API_BASE_URL}/api/blogs?category=${encodeURIComponent(category)}`;

      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch blogs');

      const data = await response.json();
      setBlogs(data.blogs || []);
      setError(null);
      trackEvent("blogs_loaded", {
        category: category || "All",
        count: (data.blogs || []).length,
      });
    } catch (err) {
      console.error('Error fetching blogs:', err);
      setError(err.message);
      // Fallback to hardcoded data if API fails
      setBlogs(fallbackBlogPosts);
      trackEvent("blogs_load_failed", {
        category: category || "All",
        reason: err?.message || "unknown",
      });
      trackException("blogs_fetch_failed");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/blogs/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || ["All"]);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      // Keep default categories
    }
  };

  const featuredPost = blogs.find((post) => post.featured);
  const regularPosts = blogs.filter((post) => !post.featured);

  return (
    <main data-testid="blog-page" className="pt-24 md:pt-32 bg-[#050505]">
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-xs font-mono tracking-wider uppercase text-[#00C896]/80 mb-4 block">
            Insights & Resources
          </span>
          <h1
            className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-white mb-6"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            Blog
          </h1>
          <p className="text-lg md:text-xl text-slate-400 leading-relaxed max-w-2xl">
            Expert insights on startup finance, tax strategy, and growth.
            Stay ahead with actionable advice from our team.
          </p>
        </motion.div>

        {/* Categories */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-wrap gap-3 mt-10"
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => {
                trackEvent("blog_category_selected", {
                  category,
                });
                setSelectedCategory(category);
              }}
              data-testid={`blog-category-${category.toLowerCase().replace(' ', '-')}`}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${category === selectedCategory
                ? "bg-[#00C896] text-black"
                : "bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white"
                }`}
            >
              {category}
            </button>
          ))}
        </motion.div>
      </section>

      {/* Loading State */}
      {loading && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16">
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#00C896]" />
            <span className="ml-3 text-slate-400">Loading blogs...</span>
          </div>
        </section>
      )}

      {/* Error State */}
      {error && !loading && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16">
          <div className="text-center py-10">
            <p className="text-yellow-500 mb-2">Unable to load from API. Showing cached content.</p>
          </div>
        </section>
      )}

      {/* Featured Post */}
      {!loading && featuredPost && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16">
          <motion.article
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to={`/blog/${featuredPost.slug}`}
              data-testid="featured-blog-post"
              className="group block bg-[#0A0A0A] rounded-3xl overflow-hidden border border-white/5 hover:border-[#00C896]/20 transition-all"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2">
                {/* Image */}
                <div className="relative h-72 lg:h-auto overflow-hidden">
                  <img
                    src={featuredPost.image}
                    alt={featuredPost.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0A0A0A]/50 lg:hidden" />
                </div>

                {/* Content */}
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <span className="text-xs font-mono tracking-wider uppercase text-[#00C896] mb-4">
                    Featured • {featuredPost.category}
                  </span>
                  <h2
                    className="text-3xl md:text-4xl font-semibold text-white mb-4 group-hover:text-[#00C896] transition-colors"
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  >
                    {featuredPost.title}
                  </h2>
                  <p className="text-lg text-slate-400 leading-relaxed mb-6">
                    {featuredPost.excerpt}
                  </p>

                  <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-6">
                    <span className="flex items-center gap-2">
                      <User size={14} />
                      {featuredPost.author}
                    </span>
                    <span className="flex items-center gap-2">
                      <Calendar size={14} />
                      {featuredPost.date}
                    </span>
                    <span className="flex items-center gap-2">
                      <Clock size={14} />
                      {featuredPost.readTime}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-[#00C896] font-medium group-hover:gap-3 transition-all">
                    <span>Read Article</span>
                    <ArrowRight size={16} />
                  </div>
                </div>
              </div>
            </Link>
          </motion.article>
        </section>
      )}

      {/* Regular Posts Grid */}
      {!loading && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {regularPosts.map((post, index) => (
              <motion.article
                key={post.id || post.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link
                  to={`/blog/${post.slug}`}
                  data-testid={`blog-card-${post.slug}`}
                  className="blog-card group block h-full bg-[#0A0A0A] rounded-2xl overflow-hidden border border-white/5 hover:border-[#00C896]/20 transition-all"
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="blog-card-image w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] to-transparent" />
                    <span className="absolute bottom-4 left-4 text-xs font-mono tracking-wider uppercase text-[#00C896]">
                      {post.category}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3
                      className="text-xl font-semibold text-white mb-3 group-hover:text-[#00C896] transition-colors line-clamp-2"
                      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                    >
                      {post.title}
                    </h3>
                    <p className="text-slate-400 text-sm leading-relaxed mb-4 line-clamp-2">
                      {post.excerpt}
                    </p>

                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      <span>{post.date}</span>
                      <span>•</span>
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>

          {/* Empty State */}
          {regularPosts.length === 0 && !featuredPost && !loading && (
            <div className="text-center py-20">
              <p className="text-slate-400 text-lg">No blog posts found in this category.</p>
            </div>
          )}
        </section>
      )}

      {/* Newsletter CTA */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2
              className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-4"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              Subscribe to Our Newsletter
            </h2>
            <p className="text-slate-400 leading-relaxed mb-8">
              Get the latest insights on startup finance, tax strategies, and
              growth delivered to your inbox.
            </p>
            <form
              data-testid="newsletter-form"
              className="flex flex-col sm:flex-row gap-4"
              onSubmit={(e) => e.preventDefault()}
            >
              <input
                type="email"
                placeholder="Enter your email"
                data-testid="newsletter-email"
                className="flex-1 bg-white/5 border border-white/10 rounded-full px-6 py-3 text-white placeholder:text-slate-600 focus:border-[#00C896]/50 focus:outline-none focus:ring-2 focus:ring-[#00C896]/20"
              />
              <button
                type="submit"
                data-testid="newsletter-submit"
                className="bg-[#00C896] text-black font-bold px-8 py-3 rounded-full hover:bg-[#00E6AC] transition-colors whitespace-nowrap"
              >
                Subscribe
              </button>
            </form>
          </motion.div>
        </div>
      </section>
    </main>
  );
}

// Fallback data in case API is unavailable
const fallbackBlogPosts = [
  {
    id: 1,
    slug: "series-a-readiness-checklist",
    title: "The Ultimate Series A Readiness Checklist for 2025",
    excerpt: "What every founder needs to know before approaching institutional investors. A comprehensive guide to financial, legal, and operational preparedness.",
    category: "Fundraising",
    author: "Victoria Sterling",
    date: "December 10, 2025",
    readTime: "8 min read",
    image: "https://images.unsplash.com/photo-1762279389020-eeeb69c25813?w=800&q=80",
    featured: true,
  },
  {
    id: 2,
    slug: "qsbs-guide-founders",
    title: "QSBS Explained: How Founders Can Save Millions in Taxes",
    excerpt: "Qualified Small Business Stock (QSBS) can provide up to $10M in tax-free gains. Learn how to qualify and maximize this powerful tax benefit.",
    category: "Tax Strategy",
    author: "Marcus Chen",
    date: "December 5, 2025",
    readTime: "6 min read",
    image: "https://images.unsplash.com/photo-1762279389002-7b6abd7bd6c6?w=800&q=80",
    featured: false,
  },
  {
    id: 3,
    slug: "fractional-cfo-guide",
    title: "When Should Your Startup Hire a Fractional CFO?",
    excerpt: "Understanding the right time to bring in financial leadership can make or break your growth trajectory. Here's what to look for.",
    category: "CFO Insights",
    author: "James Okonkwo",
    date: "November 28, 2025",
    readTime: "5 min read",
    image: "https://images.unsplash.com/photo-1692607520918-7d8bb85e7e64?w=800&q=80",
    featured: false,
  },
  {
    id: 4,
    slug: "cash-flow-management-startups",
    title: "Cash Flow Management: The Founder's Survival Guide",
    excerpt: "90% of startups fail due to cash flow issues. Learn the strategies that keep high-growth companies financially healthy.",
    category: "Finance",
    author: "Victoria Sterling",
    date: "November 20, 2025",
    readTime: "7 min read",
    image: "https://images.unsplash.com/photo-1734971702269-60d36fc28829?w=800&q=80",
    featured: false,
  },
];
